import * as React from "react";

import { GridSize } from "@material-ui/core";
import * as Redux from "react-redux";

import { InputMask } from "client/domain/FormBuilder/fields/InputField";
import {
  AllMerchantFieldMapper,
  FieldErrorMap,
  FieldSets,
  MerchantError,
  MerchantFieldMapper,
} from "client/lib/apt-merchant";
import merge from "client/lib/deepmerge";
import { RootState } from "client/store";

export type UIFieldMap = {
  customHelperText?: React.ReactNode;
  customFieldHeader?: React.ReactNode;
  inputMask?: InputMask;
  order?: number;
  width?: GridSize;
  pageNumber?: 1 | 2 | 3 | 4; // max is length of forms in oneqaat
} & Partial<MerchantError>;

export type BaseFieldMap = MerchantFieldMapper<UIFieldMap>;

export function generateOrderedFieldMap<
  T extends AllMerchantFieldMapper<UIFieldMap>
>(fieldSet: T): T {
  const unorderedUiFieldMap = Object.entries(fieldSet);
  const orderedUiFieldMap = {} as AllMerchantFieldMapper<UIFieldMap>;
  unorderedUiFieldMap.forEach(([prop, opts], i) => {
    orderedUiFieldMap[prop as keyof AllMerchantFieldMapper<UIFieldMap>] = {
      ...opts,
      order: i,
    };
  });
  return orderedUiFieldMap as T;
}

export const createOrderedUiFieldMap = ({
  cashPay = {},
  companyInformation = {},
  customerSupport = {},
  lead = {},
  locations = [],
  owners = [],
  primaryBank = {},
  secondaryBank = {},
  storeDetails = {},
}: BaseFieldMap): BaseFieldMap => {
  const orderedUiFieldMap = {
    cashPay: generateOrderedFieldMap<
      Partial<FieldSets.CashPayFieldSetValueMapper<UIFieldMap>>
    >(cashPay),
    companyInformation: generateOrderedFieldMap<
      Partial<FieldSets.CompanyInformationFieldSetValueMapper<UIFieldMap>>
    >(companyInformation),
    customerSupport: generateOrderedFieldMap<
      Partial<FieldSets.CustomerSupportFieldSetValueMapper<UIFieldMap>>
    >(customerSupport),
    lead: generateOrderedFieldMap<
      Partial<FieldSets.LeadFieldSetValueMapper<UIFieldMap>>
    >(lead),
    locations: (locations || []).map((location) =>
      generateOrderedFieldMap<
        Partial<FieldSets.StoreLocationValueMapper<UIFieldMap>>
      >(location)
    ),
    owners: (owners || []).map((owner) =>
      generateOrderedFieldMap<
        Partial<FieldSets.BusinessOwnerValueMapper<UIFieldMap>>
      >(owner)
    ),
    primaryBank: generateOrderedFieldMap<
      Partial<FieldSets.BankFieldSetValueMapper<UIFieldMap>>
    >(primaryBank),
    secondaryBank: generateOrderedFieldMap<
      Partial<FieldSets.BankFieldSetValueMapper<UIFieldMap>>
    >(secondaryBank),
    storeDetails: generateOrderedFieldMap<
      Partial<FieldSets.StoreDetailsFieldSetValueMapper<UIFieldMap>>
    >(storeDetails),
  } as BaseFieldMap;

  return orderedUiFieldMap;
};

export type FieldMaps = AllMerchantFieldMapper<UIFieldMap>;
export function hasProp<K extends PropertyKey>(
  // eslint-disable-next-line @typescript-eslint/ban-types
  data: object,
  prop: K
): data is Record<K, unknown> {
  return prop in data;
}

const isBaseFieldMap = (fieldMap: unknown): fieldMap is BaseFieldMap => {
  if (typeof fieldMap !== "object") {
    throw new Error(`Invalid fieldMap passed to isBaseFieldMap`);
  }
  if (fieldMap == null) {
    throw new Error("isBaseFieldMap cannot except null parameters");
  }
  if (
    ![
      "cashPay",
      "companyInformation",
      "customerSupport",
      "lead",
      "locations",
      "owners",
      "primaryBank",
      "secondaryBank",
      "storeDetails",
    ].some((val) => hasProp(fieldMap, val))
  ) {
    throw new Error("isBaseFieldMap not supplied in correct shape");
  }
  return true;
};

export const mergeErrorsIntoFieldMap = (merchantErrors: FieldErrorMap) => (
  currentFieldMap: unknown
): BaseFieldMap => {
  const newFieldMap = merge(currentFieldMap, merchantErrors);
  if (isBaseFieldMap(newFieldMap)) {
    return newFieldMap;
  }
  throw new Error("mergeErrorsIntoFieldMap did not produce a correct fieldMap");
};

export function useUiFieldMap(
  baseFieldMap: BaseFieldMap
): {
  updateUiFieldMap: (updatedFieldMap: BaseFieldMap) => void;
  orderedUiFieldMap: BaseFieldMap;
} {
  const [
    orderedUiFieldMap,
    setOrderedUiFieldMap,
  ] = React.useState<BaseFieldMap>(createOrderedUiFieldMap(baseFieldMap));

  const {
    error: { fieldErrorMap },
  } = Redux.useSelector(({ error }: RootState) => ({ error }));

  React.useEffect(() => {
    // anytime fieldErrorMap changes, update the uiFieldMap to append errors per field
    setOrderedUiFieldMap(mergeErrorsIntoFieldMap(fieldErrorMap));
  }, [fieldErrorMap]);

  return {
    orderedUiFieldMap: orderedUiFieldMap,
    updateUiFieldMap: (newFieldMap: BaseFieldMap) => {
      setOrderedUiFieldMap(createOrderedUiFieldMap(newFieldMap));
    },
  };
}
